import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { PDFPrintGenerator } from '@ruby/configs/common.constants';
import { NSDiversionsConstants } from '@ruby/configs/diversions.constants';
import { EDiversionsGridPaths, EDiversionsSubmitDetailsType, EDiversionsTypes } from '@ruby/shared/enums/diversions.enum';
import { IGridConfigurations } from '@ruby/shared/models/commons/grid.interface';
import {
  IDiversionsDestTurnoverForm,
  IDiversionsOrigTurnoverForm,
  IDiversionsPrivateCarForm,
  IDiversionsRequestForm,
  IDiversionsRequestSummary,
  IDiversionsReshipForm
} from '@ruby/shared/models/diversions/diversions.interface';

@Component({
  selector: 'ng-diversions-submit-details',
  templateUrl: './diversions-submit-details.component.html',
  styleUrls: ['./diversions-submit-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DiversionsSubmitDetailsComponent implements OnChanges {
  @Input() summary?: IDiversionsRequestSummary;
  @Input() requestForm?: IDiversionsRequestForm;
  @Input() privateForm?: IDiversionsPrivateCarForm;
  @Input() originTurnoverForm?: IDiversionsOrigTurnoverForm;
  @Input() destTuroverForm?: IDiversionsDestTurnoverForm;
  @Input() reshipForm?: IDiversionsReshipForm;
  @Input() reshipCars!: Array<string>;
  @Input() type!: EDiversionsSubmitDetailsType;
  @Output() submitAnotherRequest = new EventEmitter<IDiversionsRequestForm>();
  @Output() submitAnotherPrivateCar = new EventEmitter<IDiversionsPrivateCarForm>();
  @Output() submitOriginTurnover = new EventEmitter<IDiversionsOrigTurnoverForm>();
  @Output() submitAnotherDestTurnover = new EventEmitter<IDiversionsDestTurnoverForm>();
  @Output() submitAnotherReship = new EventEmitter<IDiversionsReshipForm>();
  @ViewChild('printContainer') printContainer!: ElementRef;
  configs: IGridConfigurations = NSDiversionsConstants.gridPageConfigs[EDiversionsGridPaths.SUBMIT_DETAILS];
  form: UntypedFormGroup = new UntypedFormGroup({});
  types: typeof EDiversionsTypes = EDiversionsTypes;
  submitType = EDiversionsSubmitDetailsType;
  gridPaths: typeof EDiversionsGridPaths = EDiversionsGridPaths;

  constructor() {}

  get routesFormArray(): UntypedFormArray {
    return this.form.get('route.routes')! as UntypedFormArray;
  }

  ngOnChanges(changes: SimpleChanges): void {
    let type: EDiversionsTypes = EDiversionsTypes.DIVERSION;
    let formValue:
      IDiversionsRequestForm
      | IDiversionsPrivateCarForm
      | IDiversionsDestTurnoverForm
      | IDiversionsOrigTurnoverForm
      | IDiversionsReshipForm = NSDiversionsConstants.diversionsEmptyRequestForm;
    if (changes.requestForm && changes.requestForm.currentValue) {
      type = EDiversionsTypes.DIVERSION;
      formValue = changes.requestForm.currentValue;
    } else if (changes.privateForm && changes.privateForm.currentValue) {
      type = EDiversionsTypes.EMPTY_CAR_DISPOSITION;
      formValue = changes.privateForm.currentValue;
    } else if (changes.destTuroverForm && changes.destTuroverForm.currentValue) {
      type = EDiversionsTypes.DESTINATION_TURNOVER;
      formValue = changes.destTuroverForm.currentValue;
    } else if (changes.originTurnoverForm && changes.originTurnoverForm.currentValue) {
      type = EDiversionsTypes.ORIGIN_TURNOVER;
      formValue = changes.originTurnoverForm.currentValue;
    } else if (changes.reshipForm && changes.reshipForm.currentValue) {
      type = EDiversionsTypes.RESHIP;
      formValue = changes.reshipForm.currentValue;
    }
    if (formValue !== NSDiversionsConstants.diversionsEmptyRequestForm) {
      this.buildForm(type, formValue);
    }
  }

  /**
   * Print Preview
   *
   * @summary Prints the displaying submit details
   * @returns void
   */

  // @TODO Fix this
  /* istanbul ignore next */
  printPreview(): void {
    PDFPrintGenerator.print('Submit Details', this.printContainer.nativeElement.innerHTML, 'diversions-submit-details');
  }

  /**
   * Submit Another
   *
   * @summary Emit submit another request event
   * @returns void
   */
  submitAnother(): void {
    switch (this.summary!.requestType) {
      case EDiversionsTypes.DIVERSION:
        this.submitAnotherRequest.emit(this.requestForm);
        break;
      case EDiversionsTypes.EMPTY_CAR_DISPOSITION:
        this.submitAnotherPrivateCar.emit(this.privateForm);
        break;
      case EDiversionsTypes.ORIGIN_TURNOVER:
        this.submitOriginTurnover.emit(this.originTurnoverForm);
        break;
      case EDiversionsTypes.DESTINATION_TURNOVER:
        this.submitAnotherDestTurnover.emit(this.destTuroverForm);
        break;
      case EDiversionsTypes.RESHIP:
        this.submitAnotherReship.emit(this.reshipForm);
        break;
    }
  }

  /**
   * Navigate Reship
   *
   * @summary Navigates to reship
   * @returns void
   */
  navigateReship(): void {
    this.submitAnotherReship.emit({
      cars: this.reshipCars.join('\n'),
      consignee: this.requestForm!.consignee,
      route: this.requestForm!.route,
      billTo: this.requestForm!.billTo,
      personAuthorizing: this.requestForm!.personAuthorizing,
      crossBorder: this.requestForm!.crossBorder
    } as IDiversionsReshipForm);
  }

  private buildForm(type: EDiversionsTypes,
                    form: IDiversionsRequestForm | IDiversionsPrivateCarForm | IDiversionsDestTurnoverForm | IDiversionsOrigTurnoverForm | IDiversionsReshipForm): void {
    switch (type) {
      case EDiversionsTypes.DIVERSION:
        this.form = NSDiversionsConstants.forms[EDiversionsTypes.DIVERSION]();
        this.addJunctions(form as IDiversionsRequestForm);
        NSDiversionsConstants.addCrossBorder(EDiversionsTypes.DIVERSION, this.form, undefined, form as IDiversionsRequestForm);
        break;
      case EDiversionsTypes.EMPTY_CAR_DISPOSITION:
        this.form = NSDiversionsConstants.forms[EDiversionsTypes.EMPTY_CAR_DISPOSITION]();
        this.addJunctions(form as IDiversionsPrivateCarForm);
        NSDiversionsConstants.addCrossBorder(EDiversionsTypes.EMPTY_CAR_DISPOSITION, this.form, undefined, form as IDiversionsPrivateCarForm);
        break;
      case EDiversionsTypes.DESTINATION_TURNOVER:
        this.form = NSDiversionsConstants.forms[EDiversionsTypes.DESTINATION_TURNOVER]();
        break;
      case EDiversionsTypes.ORIGIN_TURNOVER:
        this.form = NSDiversionsConstants.forms[EDiversionsTypes.ORIGIN_TURNOVER]();
        break;
      case EDiversionsTypes.RESHIP:
        this.form = NSDiversionsConstants.forms[EDiversionsTypes.RESHIP]();
        this.addJunctions(form as IDiversionsReshipForm);
        NSDiversionsConstants.addCrossBorder(EDiversionsTypes.RESHIP, this.form, undefined, form as IDiversionsReshipForm);
        break;
    }

    this.handlePersonAuthCustomer(form);
    this.form.patchValue(form);
  }

  private handlePersonAuthCustomer(form:
                                     IDiversionsRequestForm
                                     | IDiversionsPrivateCarForm
                                     | IDiversionsDestTurnoverForm
                                     | IDiversionsReshipForm
                                     | IDiversionsOrigTurnoverForm): void {
    if (form.personAuthorizing.customer) {
      (this.form.get('personAuthorizing') as UntypedFormGroup).addControl('customer', new UntypedFormControl({ value: '', disabled: true }, Validators.required));
    } else if (form.personAuthorizing.customerName) {
      (this.form.get('personAuthorizing') as UntypedFormGroup).addControl('customerName', new UntypedFormControl({
        value: '',
        disabled: true
      }, Validators.required));
    }
  }

  private addJunctions(form: IDiversionsRequestForm | IDiversionsPrivateCarForm | IDiversionsReshipForm): void {
    if (form.route.routes && form.route.routes.length > 1) {
      this.routesFormArray.clear();
      form.route.routes.forEach(() => {
        this.routesFormArray.push(NSDiversionsConstants.buildJunctionFormGroup(this.routesFormArray.length));
      });
    }
  }
}
